/**
 * Mob-bump feel config — live-tunable (smoothness arc Phase 6, 2026-05-29).
 *
 * When the local player walks into a mob, the client resolves a fraction of
 * the overlap onto the PLAYER (never the mob — DJ rejected the old "tractor"
 * shove), damps the player's velocity, and adds a touch of jitter so repeated
 * contacts don't feel mechanical. The shallow residual overlap lets the
 * server's applyPlayerBump give the mob its small share → reads as a mutual,
 * WoW-style solid bump (not MapleStory pass-through, not an immovable wall).
 *
 * Exposed as a live store so DJ can tune the feel from the GUI (the in-game
 * 🎛 Interp Tuning debug tool's "Mob bump feel" section) without a reload —
 * PredictionLoop.resolveMobCollision reads these by reference each frame.
 * The server-side share (applyPlayerBump in MobAISystem) is a separate
 * shared-config sub-step; the client resistance is the visible half.
 */

export interface MobBumpConfig {
  /** Fraction of overlap pushed off the player per frame (0=none, 1=full). */
  resolve: number;
  /** Velocity retained after a bump (1=no damp, 0=dead stop). */
  damp: number;
  /** ± random separation jitter (px) for organic feel. */
  randomPx: number;
}

export const MOB_BUMP_DEFAULT: Readonly<MobBumpConfig> = {
  resolve: 0.5,
  damp: 0.5,
  randomPx: 6,
};

/** Live, mutable store read by PredictionLoop each frame + written by the GUI. */
export const mobBumpConfig: MobBumpConfig = { ...MOB_BUMP_DEFAULT };

export function setMobBump(partial: Partial<MobBumpConfig>): void {
  Object.assign(mobBumpConfig, partial);
}

export function resetMobBump(): void {
  Object.assign(mobBumpConfig, MOB_BUMP_DEFAULT);
}

if (typeof window !== 'undefined') {
  (window as any).__MOB_BUMP_CONFIG__ = mobBumpConfig;
}
